/* global safeArray, t */
// EIENDELER — postene som krever en vurdering
//
// Alt skjermen har flagget, samlet i én liste med stabile id-er. Skilt ut fra
// backend-assets-recon-parts.jsx, som passerte 400 linjer.
//
// Id-en er nøkkelen vurderingen lagres på (`lineAssessments`) og må derfor
// overleve en ny innlasting: den bygges av kilden posten kommer fra — bilagets
// id, eiendelens id, saldogruppen — aldri av radnummeret. Bygde vi den av
// posisjonen i lista, ville en ny eiendel flyttet alle vurderingene under den.

// ------------------------------------------------------------------
// Postene som krever en vurdering
// ------------------------------------------------------------------
// Alt skjermen har flagget, samlet i én liste med stabile id-er. Id-en er
// nøkkelen vurderingen lagres på (`lineAssessments`), og må derfor overleve en
// ny innlasting: den bygges av kilden posten kommer fra, aldri av radnummeret.
const assetsReconItems = ({ assets, acquisitions, depreciation, disposals, directPostings, periodLabel }) => {
  const items = [];
  const push = (item) => items.push({ periodLabel, flags: [], ...item });

  for (const row of acquisitions) {
    if (!row.flags.length) continue;
    push({
      id: 'eiendel:ansk:' + row.id,
      type: 'aktivering',
      title: row.treatment === 'aktivert'
        ? (row.description
          ? t('rca.item.acqCapitalizedDesc', '{supplier} — aktivert kjøp ({description})', { supplier: row.supplier, description: row.description })
          : t('rca.item.acqCapitalized', '{supplier} — aktivert kjøp', { supplier: row.supplier }))
        : (row.description
          ? t('rca.item.acqExpensedDesc', '{supplier} — kostnadsført kjøp ({description})', { supplier: row.supplier, description: row.description })
          : t('rca.item.acqExpensed', '{supplier} — kostnadsført kjøp', { supplier: row.supplier })),
      date: row.date,
      amount: row.net,
      accountCode: row.accountCode,
      assetId: row.assetId,
      flags: row.flags,
    });
  }

  for (const asset of assets) {
    if (asset.flags.includes('mangler_dok')) {
      push({
        id: 'eiendel:dok:' + asset.id,
        type: 'dokumentasjon',
        title: t('rca.item.docMissing', '{name} — kjøpsdokumentasjon mangler', { name: asset.name }),
        date: asset.acquiredDate,
        amount: asset.cost,
        accountCode: asset.accountCode,
        assetId: asset.id,
        flags: ['mangler_dok'],
      });
    }
    if (asset.flags.includes('uten_avskrivning')) {
      push({
        id: 'eiendel:plan:' + asset.id,
        type: 'avskrivning',
        title: t('rca.item.noDepreciation', '{name} — ingen avskrivning beregnet for {year}', { name: asset.name, year: depreciation.year }),
        date: asset.acquiredDate,
        amount: asset.bookValue,
        accountCode: asset.accountCode,
        assetId: asset.id,
        flags: ['uten_avskrivning'],
      });
    }
    if (asset.flags.includes('fullt_avskrevet')) {
      push({
        id: 'eiendel:levetid:' + asset.id,
        type: 'levetid',
        title: t('rca.item.fullyDepreciated', '{name} — fullt avskrevet og fortsatt i registeret', { name: asset.name }),
        date: asset.acquiredDate,
        amount: 0,
        accountCode: asset.accountCode,
        assetId: asset.id,
        flags: ['fullt_avskrevet'],
      });
    }
    if (asset.flags.includes('negativ_verdi')) {
      push({
        id: 'eiendel:negativ:' + asset.id,
        type: 'avskrivning',
        title: t('rca.item.negativeValue', '{name} — negativ bokført verdi i registeret', { name: asset.name }),
        date: asset.acquiredDate,
        amount: asset.bookValue,
        accountCode: asset.accountCode,
        assetId: asset.id,
        flags: ['negativ_verdi'],
      });
    }
  }

  for (const row of depreciation.rows) {
    // `ikke_bokfort_enda` er ikke et funn: avskrivningen bokføres i
    // årsoppgjøret, og differansen står som en navngitt linje i
    // hovedavstemmingen. Reises den som en post, må den vurderes hver måned
    // uten at det finnes noe å konkludere.
    if (row.status !== 'differanse') continue;
    push({
      id: 'eiendel:avskr:' + row.group,
      type: 'avskrivning',
      title: row.booked > 0.005
        ? t('rca.item.depDeviates', '{label} — bokført avskrivning avviker fra registeret', { label: row.label })
        : t('rca.item.depNotBooked', '{label} — årets avskrivning er ikke bokført', { label: row.label }),
      date: '',
      amount: row.difference,
      accountCode: row.account,
      assetId: null,
      flags: ['avskr_diff'],
    });
  }

  for (const row of disposals) {
    push({
      id: 'eiendel:avgang:' + row.id,
      type: 'avgang',
      title: row.kind === 'uregistrert'
        ? t('rca.item.unregisteredDisposal', '{name} — kreditføring på anleggskonto uten registrert avgang', { name: row.name })
        : t('rca.item.registeredDisposal', '{name} — avgang registrert i perioden', { name: row.name }),
      date: row.date,
      amount: row.saleAmount,
      accountCode: row.accountCode,
      assetId: row.assetId,
      flags: row.kind === 'uregistrert' ? row.flags : ['avgang_registrert', ...row.flags],
    });
  }

  for (const row of directPostings) {
    push({
      id: 'eiendel:direkte:' + row.id,
      type: 'direkteforing',
      title: row.description
        ? t('rca.item.directPostingDesc', 'Direkteføring på konto {account} — {description}', { account: row.accountCode, description: row.description })
        : t('rca.item.directPosting', 'Direkteføring på konto {account}', { account: row.accountCode }),
      date: row.date,
      amount: row.amount,
      accountCode: row.accountCode,
      assetId: null,
      flags: ['direkteforing'],
    });
  }

  // Samme forhold kan bli flagget to ganger — et kjøp på anleggskonto uten
  // eiendelspost er både en anskaffelse og en direkteføring. Postene slås
  // sammen på id, slik at den som vurderer ikke må vurdere det samme to ganger.
  const byId = new Map();
  for (const item of items) {
    const existing = byId.get(item.id);
    if (!existing) { byId.set(item.id, item); continue; }
    existing.flags = [...new Set([...existing.flags, ...item.flags])];
  }
  return [...byId.values()].sort((left, right) => String(right.date).localeCompare(String(left.date)));
};

Object.assign(window, { assetsReconItems });
